import { apiRequest } from './client';

export interface Jurisdiction {
  id: string;
  name: string;
  code: string;
  type?: string;
  country_name?: string;
  continent_name?: string;
  created_at?: string | null;
}

export interface RegulationSummary {
  id: string;
  title: string;
  title_short?: string;
  jurisdiction_name: string;
  jurisdiction_code: string;
  jurisdiction_type?: string;
  regulation_type?: string;
  status?: string;
  last_action_date?: string;
  effective_date?: string;
  source_url?: string;
  summary?: string;
  content?: string;
  content_format?: string;
  content_type?: string;
  content_warning?: string | null;
  /** Account-specific rank and why (only with sort_by=relevance). */
  relevance?: number | null;
  relevance_reasons?: string[] | null;
}

export interface RegulationDetail extends RegulationSummary {}

export interface TimelineItem {
  id: string;
  title: string;
  title_short?: string;
  jurisdiction_name: string;
  jurisdiction_code: string;
  date: string;
  date_type: 'effective_date' | 'last_action_date';
  regulation_type?: string;
  status?: string;
  last_action_date?: string;
  effective_date?: string;
  source_url?: string;
  summary?: string;
}

export interface TrendDataPoint {
  period: string;
  regulations_count: number;
  by_type: Record<string, number>;
  by_status: Record<string, number>;
  by_source?: Record<string, number>;
  by_jurisdiction_type?: Record<string, number>;
}

export interface JurisdictionStats {
  jurisdiction_id: string;
  jurisdiction_name: string;
  jurisdiction_code: string;
  country_iso2?: string | null;
  regulation_count: number;
  avg_relevance_score?: number | null;
  // Most recent regulatory activity in this jurisdiction (data recency for the map).
  most_recent_regulation_date?: string | null;
}

export interface ActivityEvent {
  id: string;
  timestamp: string;
  event_type: string; // e.g. 'regulation', 'admin'
  title: string;
  description?: string;
  jurisdiction_name?: string;
  jurisdiction_code?: string;
  user?: string;
}

export interface NewsletterArticle {
  /** Article id — present so admins can delete a noisy article from the feed. */
  id?: string | null;
  source_key: string;
  source_name: string;
  country: string;
  title: string;
  url: string;
  author?: string;
  published_date?: string | null;
  scraped_at: string;
  created_at?: string | null;
  updated_at?: string | null;
  image_url?: string | null;
  /** SRS #27: policy topic keys */
  topics?: string[];
  /** SRS #31: article | podcast | video | linkedin */
  media_kind?: 'article' | 'podcast' | 'video' | 'linkedin';
  share?: { linkedin: string; x: string; email: string };
}

export type NewsletterFrequency = 'none' | 'daily' | 'weekly' | 'monthly';

export interface NewsletterPreference {
  frequency: NewsletterFrequency;
  weekly_enabled?: boolean;
  preferred_day: 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
  /** SRS #28 personalisation — omitted on PUT means "leave unchanged". */
  countries?: string[];
  topics?: string[];
  sources?: string[];
  industries?: string[];
  /** SRS #30 monthly digests: day of month 1–28. */
  monthly_day?: number;
  /** What the digest is actually built on once profile fallbacks apply (read-only). */
  effective?: { countries: string[]; topics: string[]; sources: string[]; industries: string[]; explicit: boolean } | null;
  detail?: string | null;
}

export interface NewsletterOptions {
  frequencies: NewsletterFrequency[];
  countries: string[];
  topics: { key: string; label: string }[];
  sources: { source_key: string; name: string; country: string }[];
  industries: string[];
  weekdays: string[];
}

export const getNewsletterOptions = async (): Promise<NewsletterOptions> =>
  apiRequest<NewsletterOptions>('/api/v1/users/newsletter-options');

export interface RegulationAlertPreference {
  enabled: boolean;
  detail?: string | null;
}

// Helper to build query string
const buildQueryString = (params: Record<string, any>): string => {
  const filtered = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  return filtered.length > 0 ? `?${filtered.join('&')}` : '';
};

export const getRegulationDetail = async (id: string): Promise<RegulationDetail> => {
  return apiRequest<RegulationDetail>(`/api/v1/dashboard/regulations/${id}`);
};

export const getNewsletterPreference = async (token: string): Promise<NewsletterPreference> => {
  return apiRequest<NewsletterPreference>('/api/v1/users/newsletter-preferences', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const updateNewsletterPreference = async (
  token: string,
  payload: NewsletterPreference,
): Promise<NewsletterPreference> => {
  return apiRequest<NewsletterPreference>('/api/v1/users/newsletter-preferences', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
};

export const sendNewsletterPreviewNow = async (token: string): Promise<{ detail: string }> => {
  return apiRequest<{ detail: string }>('/api/v1/users/newsletter-preferences/send-now', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getRegulationAlertPreference = async (token: string): Promise<RegulationAlertPreference> => {
  return apiRequest<RegulationAlertPreference>('/api/v1/users/regulation-alert-preferences', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const updateRegulationAlertPreference = async (
  token: string,
  payload: RegulationAlertPreference,
): Promise<RegulationAlertPreference> => {
  return apiRequest<RegulationAlertPreference>('/api/v1/users/regulation-alert-preferences', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
};

export const sendRegulationAlertPreviewNow = async (token: string): Promise<{ detail: string }> => {
  return apiRequest<{ detail: string }>('/api/v1/users/regulation-alert-preferences/send-now', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

// Get all jurisdictions for filter dropdown
export const getJurisdictions = async (
  params?: { q?: string; limit?: number; country_id?: string; with_content?: boolean },
): Promise<Jurisdiction[]> => {
  const queryString = buildQueryString({
    q: params?.q,
    limit: params?.limit,
    country_id: params?.country_id,
    with_content: params?.with_content,
  });
  // Use the dashboard endpoint which includes continent_name and country_name
  const responseAny = await apiRequest<any>(`/api/v1/dashboard/jurisdictions${queryString}`);
  if (Array.isArray(responseAny)) {
    return responseAny as Jurisdiction[];
  }
  if (responseAny?.jurisdictions && Array.isArray(responseAny.jurisdictions)) {
    return responseAny.jurisdictions as Jurisdiction[];
  }
  if (responseAny?.data && Array.isArray(responseAny.data)) {
    return responseAny.data as Jurisdiction[];
  }
  return [];
};

// Get recent regulations for Active Alerts
export const getRecentRegulations = async (params: {
  days?: number;
  start_date?: string;
  end_date?: string;
  jurisdiction_id?: string;
  date_type?: 'last_action_date' | 'effective_date';
  country_id?: string;
}): Promise<{ regulations: RegulationSummary[]; total: number }> => {
  const queryString = buildQueryString({
    days: params.days,
    start_date: params.start_date,
    end_date: params.end_date,
    jurisdiction_id: params.jurisdiction_id,
    date_type: params.date_type || 'last_action_date',
    country_id: params.country_id,
  });
  
  return apiRequest<{ regulations: RegulationSummary[]; total: number }>(
    `/api/v1/dashboard/recent-regulations${queryString}`
  );
};

// Unified activity feed (regulations + admin events)
export const getActivityFeed = async (params?: {
  days?: number;
  country?: string;
}): Promise<{ items: ActivityEvent[]; total: number }> => {
  const queryString = buildQueryString({
    days: params?.days,
    country: params?.country,
  });
  return apiRequest<{ items: ActivityEvent[]; total: number }>(
    `/api/v1/dashboard/activity${queryString}`
  );
};

// Get timeline data
export const getTimeline = async (params: {
  jurisdiction_id?: string;
  start_date?: string;
  end_date?: string;
  date_type?: 'last_action_date' | 'effective_date' | 'both';
  limit?: number;
}): Promise<{ items: TimelineItem[]; total: number }> => {
  const queryString = buildQueryString({
    jurisdiction_id: params.jurisdiction_id,
    start_date: params.start_date,
    end_date: params.end_date,
    date_type: params.date_type || 'both',
    limit: params.limit || 20,
  });
  
  return apiRequest<{ items: TimelineItem[]; total: number }>(
    `/api/v1/dashboard/timeline${queryString}`
  );
};

// Get trend data for charts
export const getTrends = async (params: {
  jurisdiction_id?: string;
  start_date?: string;
  end_date?: string;
  date_type?: 'last_action_date' | 'effective_date';
  granularity?: 'month' | 'week';
  country?: string;
}): Promise<{ data: TrendDataPoint[] }> => {
  const queryString = buildQueryString({
    jurisdiction_id: params.jurisdiction_id,
    start_date: params.start_date,
    end_date: params.end_date,
    date_type: params.date_type || 'last_action_date',
    granularity: params.granularity || 'month',
    country: params.country,
  });
  return apiRequest<{ data: TrendDataPoint[] }>(
    `/api/v1/dashboard/trends${queryString}`
  );
};

// Get jurisdiction statistics for map
export const getJurisdictionStats = async (params: {
  start_date?: string;
  end_date?: string;
  date_type?: 'last_action_date' | 'effective_date';
  country?: string;
}): Promise<{ stats: JurisdictionStats[]; by_country: Record<string, number>; computed_at?: string | null }> => {
  const queryString = buildQueryString({
    start_date: params.start_date,
    end_date: params.end_date,
    date_type: params.date_type || 'last_action_date',
    country: params.country,
  });
  return apiRequest<{ stats: JurisdictionStats[]; by_country: Record<string, number>; computed_at?: string | null }>(
    `/api/v1/dashboard/jurisdiction-stats${queryString}`
  );
};

// Get regulation statistics (total)
export const getRegulationsStats = async (params?: {
  jurisdiction_id?: string;
  jurisdiction_code?: string;
  jurisdiction_type?: string;
  country_id?: string;
  start_date?: string;
  end_date?: string;
  days?: number;
  date_type?: string;
}): Promise<{ total: number }> => {
  const queryString = buildQueryString({
    jurisdiction_id: params?.jurisdiction_id,
    jurisdiction_code: params?.jurisdiction_code,
    jurisdiction_type: params?.jurisdiction_type,
    country_id: params?.country_id,
    start_date: params?.start_date,
    end_date: params?.end_date,
    days: params?.days,
    date_type: params?.date_type || 'last_action_date',
  });
  return apiRequest<{ total: number }>(
    `/api/v1/dashboard/regulations-stats${queryString}`
  );
};

// Get all regulations with filters
export const getAllRegulations = async (params: {
  jurisdiction_id?: string;
  jurisdiction_code?: string;
  jurisdiction_type?: string;
  regulation_type?: string;
  country_id?: string;
  enforcement?: 'enforced' | 'pending' | 'inactive';
  include_hidden?: boolean;
  limit?: number;
  offset?: number;
  start_date?: string;
  end_date?: string;
  days?: number;
  date_type?: string;
  sort_order?: 'asc' | 'desc';
  sort_by?: 'date' | 'title' | 'relevance';
  /** Sent so a relevance sort can rank for the signed-in account. */
  token?: string | null;
}): Promise<{ regulations: RegulationSummary[]; total: number }> => {
  const queryString = buildQueryString({
    jurisdiction_id: params.jurisdiction_id,
    jurisdiction_code: params.jurisdiction_code,
    jurisdiction_type: params.jurisdiction_type,
    regulation_type: params.regulation_type,
    country_id: params.country_id,
    enforcement: params.enforcement,
    include_hidden: params.include_hidden,
    start_date: params.start_date,
    end_date: params.end_date,
    days: params.days,
    date_type: params.date_type,
    sort_order: params.sort_order,
    sort_by: params.sort_by,
    limit: params.limit || 100,
    offset: params.offset || 0,
  });
  return apiRequest<{ regulations: RegulationSummary[]; total: number }>(
    `/api/v1/dashboard/all-regulations${queryString}`,
    params.token ? { headers: { Authorization: `Bearer ${params.token}` } } : undefined,
  );
};

// Get available filter options
export const getFilterOptions = async (): Promise<{
  jurisdiction_types: string[];
  regulation_types: string[];
}> => {
  return apiRequest<{ jurisdiction_types: string[]; regulation_types: string[] }>(
    `/api/v1/dashboard/filter-options`
  );
};

// Get total regulations count (canonical distinct artifact count)
export const getTotalRegulations = async (): Promise<{ total: number }> => {
  return apiRequest<{ total: number }>(`/api/v1/dashboard/total-regulations`);
};

// Get total jurisdictions count (from ingestion_jurisdictions)
export const getTotalJurisdictions = async (): Promise<{ total: number }> => {
  return apiRequest<{ total: number }>(`/api/v1/dashboard/total-jurisdictions`);
};

export const getNewslettersFilters = async (): Promise<{ countries: string[]; sources: string[] }> => {
  return apiRequest<{ countries: string[]; sources: string[] }>(`/api/v1/dashboard/newsletters-filters`);
};

// Get count of newsletters (if backend exposes it). Returns { total: number } or throws.
export const getNewslettersCount = async (): Promise<{ total: number }> => {
  return apiRequest<{ total: number }>(`/api/v1/dashboard/newsletters-count`);
};

export const getNewsletters = async (params?: { limit?: number; offset?: number; country?: string; topic?: string; source?: string; kind?: string; personalised?: boolean }): Promise<{ articles: NewsletterArticle[]; total: number }> => {
  const queryString = buildQueryString({
    limit: params?.limit,
    offset: params?.offset,
    country: params?.country,
    topic: params?.topic,
    source: params?.source,
    kind: params?.kind,
    personalised: params?.personalised ? 'true' : undefined,
  });
  // Auth header (when signed in) lets the server personalise; anonymous readers get the plain feed.
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('policyora_token') : null;
  return apiRequest<{ articles: NewsletterArticle[]; total: number }>(`/api/v1/dashboard/newsletters${queryString}`,
    token && params?.personalised ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
};

// Get countries count exposed by dashboard (unauthenticated)
export const getCountriesCount = async (): Promise<{ total: number }> => {
  return apiRequest<{ total: number }>(`/api/v1/dashboard/countries-count`);
};

// Country reference list (id + ISO codes) — used to map a map click (ISO-2) to
// the country UUID that jurisdiction filters expect.
export interface CountryRef {
  id: string;
  name: string;
  iso2?: string | null;
  iso3?: string | null;
}
export const getCountries = async (): Promise<CountryRef[]> => {
  return apiRequest<CountryRef[]>(`/api/v1/dashboard/countries`);
};

// Get trending topics derived from all users' chat session titles (LLM-clustered)
export interface TrendingTopic {
  label: string;
  count: number;
}

export const getTrendingTopicsFromChat = async (limit = 10): Promise<{ topics: TrendingTopic[]; total: number }> => {
  const queryString = buildQueryString({ limit });
  return apiRequest<{ topics: TrendingTopic[]; total: number }>(`/api/v1/chat/trending-topics${queryString}`);
};

// ---------------------------------------------------------------------------
// Personalised overview (SRS #9) — scoped to the caller's plan and jurisdictions
// ---------------------------------------------------------------------------

export interface OverviewJurisdiction {
  name: string;
  code: string | null;
  total: number;
  new_in_window: number;
  latest_action: string | null;
}

export interface OverviewRegulation {
  id: string;
  title: string;
  jurisdiction_name: string;
  jurisdiction_code: string | null;
  regulation_type?: string | null;
  status?: string | null;
  last_action_date?: string | null;
  effective_date?: string | null;
  source_url?: string | null;
  added_at?: string | null;
}

export interface OverviewSession {
  session_id: string;
  status: string;
  jurisdiction: string | null;
  created_at: string;
}

export interface DashboardOverview {
  plan: { tier: string | null; status: string | null; current_period_end: string | null; trial_ends_at: string | null };
  scope: { entitled_jurisdictions: string[] | null; jurisdiction_limit: number | null; reason?: 'no_selection' | 'government_unauthorised' | 'plan_covers_none' | null };
  corpus: {
    total: number; jurisdictions: number; new_in_window: number; active_in_window: number;
    upcoming_effective: number; window_days: number; upcoming_window_days: number;
    corpus_updated_at: string | null;
  };
  by_jurisdiction: OverviewJurisdiction[];
  recent: OverviewRegulation[];
  upcoming: OverviewRegulation[];
  activity: {
    window_days: number; chats: number; compliance_assessments: number; risk_assessments: number;
    governance_sessions: number; latest_compliance: OverviewSession | null; latest_risk: OverviewSession | null;
  };
  generated_at: string;
}

export function fetchDashboardOverview(token: string): Promise<DashboardOverview> {
  return apiRequest<DashboardOverview>('/api/v1/dashboard/overview', {
    headers: { Authorization: `Bearer ${token}` },
  });
}

// ---------------------------------------------------------------------------
// Trending (SRS #25) — regulations with recent movement, scoped to the account
// ---------------------------------------------------------------------------

export interface TrendingItem {
  id: string;
  title: string;
  jurisdiction_name: string;
  jurisdiction_code: string | null;
  regulation_type: string | null;
  status: string | null;
  last_action_date: string | null;
  effective_date: string | null;
  source_url: string | null;
  first_seen_at: string | null;
  trending: number;
  relevance: number;
  signals: string[];
}

export interface TrendingTopicRow {
  label: string;
  raw?: string;
  current: number;
  previous: number;
  change_pct: number | null;
}

export interface TrendingResponse {
  window_days: number;
  window_widened: boolean;
  scope: string[] | null;
  items: TrendingItem[];
  topics: { jurisdictions: TrendingTopicRow[]; types: TrendingTopicRow[] };
  generated_at: string;
}

export function fetchTrending(token: string | null, limit = 12): Promise<TrendingResponse> {
  return apiRequest<TrendingResponse>(`/api/v1/dashboard/trending?limit=${limit}`,
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
}

// ---------------------------------------------------------------------------
// Analytics (SRS #23/#24) — scoped regulatory activity + account usage
// ---------------------------------------------------------------------------

export interface AnalyticsMonth { month: string; label: string; legislative_actions: number; newly_tracked: number }
export interface UsageMonth { month: string; label: string; chats: number; assessments: number; logins: number }
export interface AnalyticsResponse {
  scope: { entitled_jurisdictions: string[] | null; plan: string | null };
  regulatory: {
    months: AnalyticsMonth[];
    status_mix: Record<string, number>;
    by_jurisdiction: { name: string; total: number; active_90d: number }[];
    by_type: { type: string; n: number }[];
    total: number;
  };
  usage: {
    scope: 'none' | 'me' | 'team';
    months: UsageMonth[];
    members?: { user_id: number; name: string; chats_30d: number; assessments_30d: number; logins_30d: number }[];
    active_members_30d?: number;
    total_members?: number;
  };
  generated_at: string;
}

export function fetchAnalytics(token: string | null): Promise<AnalyticsResponse> {
  return apiRequest<AnalyticsResponse>('/api/v1/dashboard/analytics', token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
}

// ── SRS #15: data freshness ──────────────────────────────────────────────────
// Truthful "data as of": last *successful* refresh per source and the next
// expected one. Replaces the old habit of stamping cache time as if live.
export interface DataFreshnessSource {
  source_system: string;
  label: string;
  artifact_count: number;
  last_refreshed_at: string | null;
  expected_cadence_hours: number;
  next_expected_at: string | null;
  status: 'fresh' | 'stale' | 'pending' | 'paused';
}
export interface DataFreshness {
  last_refreshed_at: string | null;
  next_expected_at: string | null;
  sources: DataFreshnessSource[];
  generated_at: string;
}
export const getDataFreshness = async (): Promise<DataFreshness> =>
  apiRequest<DataFreshness>('/api/v1/dashboard/data-freshness');

// SRS #31: one-click share targets for any URL (LinkedIn, X, email)
export const getShareLinks = async (url: string, title = ''): Promise<{ linkedin: string; x: string; email: string }> =>
  apiRequest(`/api/v1/dashboard/share-links${buildQueryString({ url, title })}`);
export const getMediaKinds = async (): Promise<{ kinds: string[] }> => apiRequest('/api/v1/dashboard/media-kinds');
